import { tokenizePath } from './paths';

// Compute error prefix for a given path using known group and collection prefixes
export function computeErrorPrefix(
    path: string,
    groupPrefixMap: Map<string, string>,
    collectionPrefixMap: Map<string, string>
): string | undefined {
    const tokens = tokenizePath(path);

    // Single-pass computation:
    // - Group-level prefix: deepest matching group path (ignores indices)
    // - Collection-level prefix: first indexed segment whose basePath has a configured prefix
    //
    // `propPath` tracks the dot-path *up to the current token*, excluding indices.
    // Example for `other.accessories[1].name`:
    // - after reading props: `other` -> `other.accessories`
    // - when we hit `[1]`, propPath is `other.accessories` (used for collectionPrefixMap lookup)
    let propPath = '';
    let groupPrefix: string | undefined;
    let collectionPrefixWithIndex: string | undefined;

    for (const token of tokens) {
        if (token.type === 'prop') {
            // Group prefixes are stored on group paths like `buyer` or `buyer.address`.
            // We keep walking the path so the *deepest* matching prefix wins.
            propPath = propPath ? `${propPath}.${token.key}` : token.key;
            const maybeGroupPrefix = groupPrefixMap.get(propPath);
            if (maybeGroupPrefix) groupPrefix = maybeGroupPrefix;
        } else if (!collectionPrefixWithIndex) {
            // Only capture the first collection prefix with an index. This matches the previous
            // behavior where we stop after the first `[n]` segment that has a configured prefix.
            const collectionPrefix = collectionPrefixMap.get(propPath);
            if (!collectionPrefix) continue;

            // Display indices are 1-based (index 0 -> "1"), matching UI expectations.
            collectionPrefixWithIndex = `${collectionPrefix} ${token.index + 1}`;
        }
    }

    if (!groupPrefix && !collectionPrefixWithIndex) return undefined;
    if (groupPrefix && collectionPrefixWithIndex) return `${groupPrefix} ${collectionPrefixWithIndex}`;
    return groupPrefix ?? collectionPrefixWithIndex;
}
